/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import React, { useContext, useState, useMemo, useRef, useEffect } from 'react';
import classNames from 'classnames';

import moment from 'moment';
import { useTranslation } from 'react-i18next';
import { useMediaQuery } from 'react-responsive';
import { useNavigate, useParams } from 'react-router-dom';

import ArrowMenu from '../../../../../ArrowMenu';

import classes from './styles.module.scss';
import FavStatus from '../../../../Header/Search/FavStatus';
import { getAuthData } from '../../../../../../helpers/authStorage';
import getResizedImageUrl from '../../../../../../helpers/getResizedImageUrl';
import { CompanyContext } from '../../../../../../context/CompanyContext';
import FormDropdown from '../../../../../Form/FormDropdown';
import AdminService from '../../../../../../services/AdminService';

import CompanyAvatar from '../../CompanyAvatar';
import NotesIcon from '../../../../../../assets/images/common/shared/notes.svg';
import NotesIconActive from '../../../../../../assets/images/common/shared/notes-active.svg';

export default function CompanyHeader({ company, refetchCompany }) {
  const { t } = useTranslation();
  const { showAddCompanyNoteModal } = useContext(CompanyContext);
  const navigate = useNavigate();
  const { '*': companyId } = useParams();

  const isTabletOrMobile = useMediaQuery({ query: '(max-width: 1024px)' });

  const { userId } = getAuthData();

  // Get current company option - we'll use fetchOptions so we don't need to manually fetch
  const currentCompanyOption = useMemo(() => {
    if (!company?.id) return null;
    return {
      value: company.id,
      label: company?.company?.name || '',
    };
  }, [company]);

  // Handle company selection
  const handleCompanyChange = (fieldName, selectedOption) => {
    if (
      selectedOption?.value &&
      selectedOption.value !== parseInt(companyId, 10)
    ) {
      navigate(`/tools/companies/company-details/${selectedOption.value}`);
      // Reload the page to ensure fresh data
      // window.location.reload();
    }
  };

  // Mock data for stats - this should come from API in real implementation
  const stats = {
    openJobs: company?.activeJobsCount || 0,
    activeCandidates: company?.activeCandidatesCount || 0,
    awaitingFeedback: company?.avaitingFeedbackCount || 0,
    placed: company?.hiresCount || 0,
    shortlistRate: company?.shortList || 0,
    hires: company?.hiresCount || 0,
    avgResponseDays: company?.sla || 0,
  };
  const [isArrowMenuVisible, setIsArrowMenuVisible] = useState(false);
  const [isNotesVisible, setIsNotesVisible] = useState(false);
  const notesContainerRef = useRef(null);

  // Get notes from company data
  const companyNotes = useMemo(() => {
    return company?.company?.companyNotes || [];
  }, [company?.company?.companyNotes]);

  const hasNotes = companyNotes.length > 0;

  // Handle click outside to close notes popup
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        notesContainerRef.current &&
        !notesContainerRef.current.contains(event.target) &&
        isNotesVisible
      ) {
        setIsNotesVisible(false);
      }
    };

    if (isNotesVisible) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isNotesVisible]);

  // Toggle notes visibility
  const handleNotesClick = (e) => {
    e.stopPropagation();
    if (hasNotes) {
      setIsNotesVisible(!isNotesVisible);
    }
  };

  const formatDateTime = (dateStr) => {
    try {
      const d = new Date(dateStr);
      return d.toLocaleString();
    } catch (_) {
      return '';
    }
  };

  const options = [
    {
      label: company?.note
        ? t(
            'dashboardComponents.Companies.CompaniesList.GridCard.options.editNote'
          )
        : t(
            'dashboardComponents.Companies.CompaniesList.GridCard.options.addNote'
          ),
      onClick: () => showAddCompanyNoteModal(company, refetchCompany),
    },
  ];

  const badges = company?.company?.companyTags?.map((tag) => tag.name);

  const renderStatItem = (value, label, isPercentage = false) => (
    <div className={classes.statItem}>
      <span
        className={classNames(classes.statValue, {
          [classes.zero]: value === '-' || value === 0,
        })}
      >
        {value}
        {isPercentage && value !== '-' && '%'}
      </span>
      <span className={classes.statLabel}>{label}</span>
    </div>
  );

  return (
    <header className={classes.CompanyHeader}>
      {/* Row 1: Avatar + Dropdown ... Controls */}
      <div className={classes.row1}>
        <div className={classes.leftSide}>
          {company?.company?.logo ? (
            <div className={classes.companyLogo}>
              <img
                src={getResizedImageUrl({
                  url: `company/${company?.company?.logo}`,
                  width: 80,
                })}
                alt="Company"
              />
            </div>
          ) : (
            <CompanyAvatar companyName={company?.company?.name} size={80} />
          )}
          <FormDropdown
            name="company"
            value={currentCompanyOption}
            setFieldValue={handleCompanyChange}
            placeholder={company?.company?.name || ''}
            width="280px"
            height={50}
            style={{ border: 'none' }}
            fetchOptions={{
              dataName: 'companiesBasicInfo',
              getDataHandler: AdminService.getCompanies,
              isCompanyList: true,
              fetchHanlderOptions: {
                pageSize: 1000,
                orderBy: 'name',
                sortOrder: 'asc',
              },
            }}
          />
        </div>
        <div className={classes.rightControls}>
          <span
            className={classNames(classes.companyStatus, {
              [classes.active]: company?.company.status === 'Active',
              [classes.prospect]: company?.company.status === 'Prospect',
              [classes.inactive]: company?.company.status === 'Inactive',
            })}
          >
            {company?.company.status ?? '-'}
          </span>
          <div
            ref={notesContainerRef}
            className={classNames(classes.notesContainer, {
              [classes.activeNotes]: hasNotes,
            })}
            onClick={handleNotesClick}
            style={{ cursor: hasNotes ? 'pointer' : 'default' }}
          >
            <img
              src={hasNotes ? NotesIconActive : NotesIcon}
              alt="Notes"
              className={classNames(classes.notesIcon, {
                [classes.activeNotes]: hasNotes,
              })}
            />
            <span>{hasNotes ? companyNotes.length : 0}</span>
            {isNotesVisible && hasNotes && (
              <div className={classes.notesPopup}>
                <div className={classes.notesList}>
                  {companyNotes.map((n) => {
                    const author = `${n?.user?.userProfile?.firstName || ''}${
                      n?.user?.userProfile?.lastName
                        ? ` ${n.user.userProfile.lastName}`
                        : ''
                    }`;
                    return (
                      <div
                        className={classes.noteItem}
                        key={n?.id || n?.createdAt}
                      >
                        <div className={classes.noteText}>{n?.note || ''}</div>
                        <div className={classes.noteMeta}>
                          <span className={classes.author}>{author}</span>
                          {n?.createdAt && (
                            <>
                              <span className={classes.separator}>/</span>
                              <span className={classes.date}>
                                {formatDateTime(n.createdAt)}
                              </span>
                            </>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
          <div className={classes.iconButtons}>
            <FavStatus
              company={company}
              width={36}
              refetch={refetchCompany}
              userId={userId}
            />
            <ArrowMenu
              width={36}
              options={options}
              isVisible={isArrowMenuVisible}
              setIsVisible={setIsArrowMenuVisible}
            >
              <div className={classes.iconBtn}>⋯</div>
            </ArrowMenu>
          </div>
        </div>
      </div>

      {/* Row 2: Tags + Info ... Stats */}
      <div className={classes.row2}>
        <div className={classes.infoColumn}>
          {badges?.length > 0 && (
            <div className={classes.badgesRow}>
              {badges?.map((badge) => (
                <span key={badge} className={classes.badge}>
                  {badge}
                </span>
              ))}
            </div>
          )}
          <div className={classes.metaRow}>
            <span className={classes.industry}>
              {company?.company?.industry?.name}
            </span>
            <span className={classes.detailItem}>
              <span className={classes.label}>
                {t(
                  'dashboardComponents.Companies.CompanyDetails.CompanyHeader.primaryContact'
                )}
                :
              </span>
              <span className={classes.value}>
                {company?.company?.primaryManager?.firstName &&
                company?.company?.primaryManager?.lastName
                  ? `${company?.company?.primaryManager?.firstName} ${
                      company?.company?.primaryManager?.lastName
                    } ${
                      company?.company?.primaryManager?.designation
                        ? `(${company?.company?.primaryManager?.designation})`
                        : ''
                    }`
                  : '-'}
              </span>
            </span>
            <span className={classes.detailItem}>
              <span className={classes.label}>
                {t(
                  'dashboardComponents.Companies.CompanyDetails.CompanyHeader.datePosted'
                )}
                :
              </span>
              <span className={classes.value}>
                {moment(company?.company?.createdAt).format('DD MMM, YYYY')}
              </span>
            </span>
          </div>
        </div>

        {!isTabletOrMobile && (
          <div className={classes.statsRow}>
            {renderStatItem(
              stats.openJobs,
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.openJobs'
              )
            )}
            {renderStatItem(
              stats.activeCandidates,
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.activeCandidates'
              )
            )}
            {renderStatItem(
              stats.hires,
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.hires'
              )
            )}
            {renderStatItem(
              stats.avgResponseDays > 0 ? stats.avgResponseDays : '-',
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.avgResponseDays'
              )
            )}
            {renderStatItem(
              stats.awaitingFeedback,
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.awaitingFeedback'
              )
            )}
            {renderStatItem(
              stats.shortlistRate > 0 ? stats.shortlistRate : '-',
              t(
                'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.shortlistRate'
              ),
              true
            )}
          </div>
        )}
      </div>

      {/* Mobile Stats */}
      {isTabletOrMobile && (
        <div className={classes.mobileStats}>
          {renderStatItem(
            stats.openJobs,
            t(
              'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.openJobs'
            )
          )}
          {renderStatItem(
            stats.activeCandidates,
            t(
              'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.activeCandidates'
            )
          )}
          {renderStatItem(
            stats.awaitingFeedback,
            t(
              'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.awaitingFeedback'
            )
          )}
          {renderStatItem(
            stats.placed,
            t(
              'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.placed'
            )
          )}
          {renderStatItem(
            stats.shortlistRate > 0 ? stats.shortlistRate : '-',
            t(
              'dashboardComponents.Companies.CompanyDetails.CompanyHeader.stats.shortlistRate'
            ),
            true
          )}
        </div>
      )}
    </header>
  );
}
